/* eslint-disable  @typescript-eslint/no-explicit-any */
import { DestroyRef, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription, tap } from 'rxjs';
import { toFixedNumber } from 'src/app/shared/extensions/number.extensions';
import { tryParseJSON } from 'src/app/shared/extensions/string.extensions';
import { IPropertyComment } from 'src/app/shared/interfaces/PropertyComment.interface';
import { TranslationService } from 'src/app/shared/transloco/services/translation.service';
import { ControlType, DataType, IExtendedControl } from '../models/IExtendedControl';

export abstract class PropertiesGeneratorBase {
    protected translationService = inject(TranslationService);
    protected subscription = new Subscription();
    private detroyRef = inject(DestroyRef);

    constructor() {
        this.detroyRef.onDestroy(() => this.subscription.unsubscribe());
    }

    protected extractElements(form: FormGroup, elements: any, parent: any = null, hasEditPermission: boolean) {
        for (const element of elements) {
            const control = new FormControl() as unknown as IExtendedControl;
            const propertySchema = element['@schema'] ?? element['schema'];
            const parentSchema = parent?.['@schema'] ?? parent?.['schema'];
            const comment = this.getComment(element, propertySchema);

            control.hasGradientOrOffset = this.hasGradientOrOffset(comment);
            control.gradient = comment.gradient ?? 1;
            control.unit = element.unit;
            control.description = element.description?.en;
            control.offset = comment.offset ?? 0;
            control.displayFormat = this.getPropertyDisplayFormat(comment);
            control.dataType = propertySchema?.['@type'];
            control.outOfSync = false;
            control.nameOfParent = parent?.name;
            control.parentSchemaType = parentSchema?.['@type'];
            control.caption = this.getCaption(element, parent);
            control.name = element.name;
            control.needsConfirmation = comment.confirmation ?? false;

            this.setControlType(control, comment);

            this.setMaxLength(control, comment);

            this.setControlDataType(form, control, propertySchema, element, comment, hasEditPermission);

            this.addControlToForm(form, control, element, parent, hasEditPermission);
        }
    }

    protected areValuesDifferent(desired: unknown, reported: unknown, isNumber: boolean): boolean {
        if (isNumber) {
            const desiredNumber = Number(desired);
            const reportedNumber = Number(reported);

            if (!Number.isNaN(desiredNumber) && !Number.isNaN(reportedNumber)) {
                return desiredNumber !== reportedNumber;
            }
        }

        return Boolean(desired) && desired !== reported;
    }

    private hasGradientOrOffset(comment: any): boolean {
        return comment.gradient != null || comment.offset != null;
    }

    private setMaxLength(control: IExtendedControl, comment: IPropertyComment): void {
        if (control.dataType == DataType.String) {
            control.maxLength = comment.maxLength ?? 255;
        }
    }

    private addControlToForm(form: FormGroup, control: IExtendedControl, element: any, parent: any, hasEditPermission: boolean): void {
        const isWriteable = element.writeable ?? true;
        const isParentWriteable = parent?.writeable ?? true;
        const isReadOnly = !(isWriteable && isParentWriteable) || !hasEditPermission;

        if (isReadOnly) {
            control.disable();
        }
        const isStandardType = control.dataType !== DataType.Object && control.dataType !== DataType.Array;
        if (isStandardType) {
            form.addControl(control.name, control);
        }
    }

    private getComment(element: any, propertySchema: any) {
        // we need to parse and combine both comments as the menu and the variable itself can have different ones
        const comment: IPropertyComment = tryParseJSON(element.comment);
        const schemaComment = tryParseJSON(propertySchema.comment, {});

        return { ...comment, ...schemaComment };
    }

    private getPropertyDisplayFormat(comment: any): number {
        let displayFormat = 0;
        if (comment.displayFormat != null) {
            const displayFormatMatch = /Dec\.(\d+)/.exec(comment.displayFormat);
            if (comment.gradient) {
                displayFormat = comment.gradient;
            } else {
                if (displayFormatMatch != null) {
                    displayFormat = Number(displayFormatMatch[1]);
                }
            }
        }
        return displayFormat;
    }

    private setControlType(control: IExtendedControl, comment: IPropertyComment): void {
        let minValue = comment.lowerValue;
        let maxValue = comment.upperValue;

        if (minValue != null) {
            minValue = toFixedNumber(minValue * control.gradient + control.offset, control.displayFormat);
        }

        if (maxValue != null) {
            maxValue = toFixedNumber(maxValue * control.gradient + control.offset, control.displayFormat);
        }

        switch (control.dataType) {
            case DataType.Enum:
                control.controlType = ControlType.DropDown;
                break;
            case DataType.Boolean:
                control.controlType = ControlType.Toggle;
                break;
            case DataType.Integer:
            case DataType.Long:
            case DataType.Float:
                control.controlType = ControlType.Slider;
                control.minValue = minValue;
                control.maxValue = maxValue;
                break;
            default:
                control.controlType = ControlType.TextBox;
                break;
        }
    }

    private setControlDataType(form: FormGroup, control: IExtendedControl, propertySchema: any, element: any, comment: IPropertyComment, hasEditPermission: boolean): void {
        this.subscription.add(
            this.translationService.afterLanguageChanged$
                .pipe(
                    tap(() => {
                        switch (control.dataType) {
                            case DataType.Object:
                                this.extractElements(form, propertySchema['fields'], element, hasEditPermission);
                                break;
                            case DataType.Array:
                                const count = comment?.count || 1;
                                const fields = [];

                                for (let i = 1; i <= count; i++) {
                                    fields.push({
                                        displayName: { en: `${control.caption} ${i}` },
                                        name: `${element.name}_${i}`,
                                        schema: { '@type': propertySchema?.elementSchema['@type'] },
                                    });
                                }
                                this.extractElements(form, fields, element, hasEditPermission);

                                break;
                            case DataType.Enum:
                                control.dropDownItems = propertySchema.enumValues.map((entity: { enumValue: any; displayName: { en: any } }) => ({
                                    value: entity.enumValue,
                                    displayName: entity?.displayName?.en,
                                }));
                                break;
                            case DataType.Integer:
                            case DataType.Long:
                                if (!isNaN(control.minValue) && !isNaN(control.maxValue)) {
                                    control.setValidators([Validators.min(control.minValue), Validators.max(control.maxValue), Validators.pattern(/^-?\d+(\.\d+)?$/)]);
                                    control.validationMessage = this.translationService.translate('properties.validation.range', { minValue: control.minValue, maxValue: control.maxValue });
                                }

                                break;
                            case DataType.Float:
                                control.setValidators([Validators.pattern(/^-?\d+(\.\d+)?$/)]);
                                control.validationMessage = this.translationService.translate('properties.validation.decimal');
                        }
                    })
                )
                .subscribe()
        );
    }

    private getCaption(element: any, parent: any): any {
        let caption = element?.displayName?.en ?? element.name;
        if (parent) {
            const captionOfParent = parent.displayName?.en ?? parent.name;
            caption = captionOfParent + '\u2B9A' + caption;
        }
        return caption;
    }
}
